export const MIN_ADMIN_PASSWORD_LENGTH = 16;

const GENERATED_PASSWORD_LENGTH = 24;

const CHARACTER_SETS = {
  lowercase: 'abcdefghijkmnopqrstuvwxyz',
  uppercase: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  digits: '23456789',
  // Kept to punctuation that survives being pasted into a web form without
  // quoting or shell-escaping surprises.
  special: '!@#$%^&*-_=+?',
};

/**
 * Mirrors the server-side rules in `coderbyte-admin-password.helper.ts`. The
 * server is authoritative; this exists so an admin sees the problem while
 * typing rather than after submitting.
 */
const PASSWORD_RULES: {
  requirement: string;
  isMet: (value: string) => boolean;
}[] = [
  {
    requirement: `be at least ${MIN_ADMIN_PASSWORD_LENGTH} characters long`,
    isMet: value => value.length >= MIN_ADMIN_PASSWORD_LENGTH,
  },
  {
    requirement: 'contain a lowercase letter',
    isMet: value => /[a-z]/.test(value),
  },
  {
    requirement: 'contain an uppercase letter',
    isMet: value => /[A-Z]/.test(value),
  },
  { requirement: 'contain a number', isMet: value => /\d/.test(value) },
  {
    requirement: 'contain a special character',
    isMet: value => /[^a-zA-Z0-9]/.test(value),
  },
];

export function findAdminPasswordFailures(password: string): string[] {
  return PASSWORD_RULES.filter(rule => !rule.isMet(password)).map(
    rule => rule.requirement
  );
}

function randomCharacter(alphabet: string): string {
  // crypto.getRandomValues rather than Math.random: this ends up guarding a
  // customer's whole Coderbyte organization.
  const [value] = window.crypto.getRandomValues(new Uint32Array(1));

  return alphabet[value % alphabet.length];
}

/**
 * Generates a password that satisfies every rule by construction.
 *
 * Seeded with one character from each required set so it can never fail
 * validation, then filled from the combined alphabet and shuffled so the
 * guaranteed characters are not always in the same positions.
 *
 * Ambiguous glyphs (`l`, `I`, `O`, `0`, `1`) are left out — this password gets
 * read off a screen and typed into Coderbyte by hand.
 */
export function generateAdminPassword(): string {
  const alphabets = Object.values(CHARACTER_SETS);
  const combined = alphabets.join('');

  const characters = alphabets.map(randomCharacter);
  while (characters.length < GENERATED_PASSWORD_LENGTH) {
    characters.push(randomCharacter(combined));
  }

  // Fisher-Yates, so position does not leak which set a character came from.
  for (let i = characters.length - 1; i > 0; i -= 1) {
    const [value] = window.crypto.getRandomValues(new Uint32Array(1));
    const j = value % (i + 1);
    [characters[i], characters[j]] = [characters[j], characters[i]];
  }

  return characters.join('');
}
